/**
 * 国优炬康 管理后台路由（8 个业务域、38 个二级菜单）
 *
 * 路由守卫:
 * 1. 未登录跳 /login
 * 2. 越权跳 /admin/deny
 * 3. 检查权限点
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { type AdminPermission } from '@/shared';

import { useAuthStore } from '@/stores/auth';
import { useBrandStore } from '@/stores/brand';

import Login from '@/views/Login.vue';
import Dashboard from '@/views/Dashboard.vue';
import Deny from '@/views/Deny.vue';
import SwitchRole from '@/views/SwitchRole.vue';
import Layout from '@/layout/MainLayout.vue';
import { MENU_MODULES } from '@/config/menu';

// 自动发现所有 views 下的 .vue 页面组件
const viewModules = import.meta.glob('@/views/**/*.vue');
const componentAliases: Record<string, () => Promise<{ default: unknown }>> = {
  'order-list': () => import('@/views/order/Order.vue'),
};

function resolveMenuComponent(moduleId: string, itemId: string) {
  if (componentAliases[itemId]) return componentAliases[itemId];

  // 约定映射：menu module id 映射到目录名，item id 映射到组件名
  // 例如 content + content-article -> views/content/Article.vue
  const moduleDir = moduleId;
  const parts = itemId.replace(`${moduleId}-`, '').split('-');
  const componentName = parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

  const candidates = [
    `/src/views/${moduleDir}/${componentName}.vue`,
    `/src/views/${moduleDir}/${componentName}/index.vue`,
  ];

  for (const key of candidates) {
    if (viewModules[key]) {
      return viewModules[key] as () => Promise<{ default: unknown }>;
    }
  }

  throw new Error(`菜单 ${itemId} 缺少真实页面组件`);
}

const explicitMenuRouteIds = new Set(['dashboard-home', 'dashboard-switch-role']);
const menuRoutes: RouteRecordRaw[] = MENU_MODULES.flatMap((module) =>
  module.children.filter((item) => !explicitMenuRouteIds.has(item.id)).map((item) => ({
    path: item.path,
    name: item.id,
    component: resolveMenuComponent(module.id, item.id),
    meta: {
      title: item.title,
      icon: item.icon,
      permission: item.permission,
      module: module.title,
    },
  })),
);

const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'Login', component: Login, meta: { public: true, title: '登录' } },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      // ───── 工作台 ─────
      { path: 'dashboard', name: 'Dashboard', component: Dashboard, meta: { title: '工作台', icon: 'House', permission: 'DASHBOARD_VIEW' as AdminPermission } },
      { path: 'dashboard/switch-role', name: 'SwitchRole', component: SwitchRole, meta: { title: '角色切换', icon: 'Refresh', permission: 'DASHBOARD_SWITCH_ROLE' as AdminPermission } },

      // ───── 二级菜单(自动从 MENU_MODULES 生成) ─────
      ...menuRoutes,
    ],
  },
  { path: '/admin/deny', name: 'Deny', component: Deny, meta: { title: '越权访问', public: true } },
  { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
];

const router = createRouter({
  // 独立后台域名直接使用根路径，例如 /goods/category。
  // Nginx 负责 history 模式的 index.html 兜底。
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to, _from, next) => {
  const brand = useBrandStore();
  document.title = `${to.meta.title ?? brand.mainTitle} · ${brand.mainTitle}运营管理后台`;

  const auth = useAuthStore();

  // 1. 公开页面(Login / Deny)直接放行
  if (to.meta.public) return next();

  // 2. 未登录跳 /login
  if (!auth.isLoggedIn) return next('/login');

  // 3. 检查权限点
  const permission = to.meta.permission as AdminPermission | undefined;
  if (permission && !auth.hasPermission(permission)) {
    return next('/admin/deny');
  }

  next();
});

export default router;
